<template>
    <div class="d-flex justify-center flex-gap-8">
        <div>
            <span  class="light-input-text">{{ $t("quoteInfo.side") }}</span>
            <v-radio-group v-model="side" class="mt-0" @change="onEmitChanges" row mandatory
                :disabled="loadingNewRfq || gridActive">
                <v-radio class="radio-label"  :label="$t('quoteInfo.buy').toUpperCase()" value="BUY">
                </v-radio>
                <v-radio class="radio-label"  :label="$t('quoteInfo.sell').toUpperCase()" value="SELL">
                </v-radio>
            </v-radio-group>
        </div>
        <div>
            <span class="light-input-text">{{ $t("quoteInfo.parity") }}</span>
            <v-radio-group v-model="ccy" class="mt-0" @change="onEmitChanges" mandatory row
                :disabled="loadingNewRfq || gridActive">
                <v-radio class="radio-label"  label="USDBRL" value="USD"> </v-radio>
                <v-radio class="radio-label"  label="EURBRL" value="EUR"> </v-radio>
                <v-radio class="radio-label"  label="JPYBRL" value="JPY"> </v-radio>
                <v-radio class="radio-label"  label="GBPBRL" value="GBP"> </v-radio>
            </v-radio-group>
        </div>
        <div>
            <span class="light-input-text">{{ $t("quoteInfo.maturityText") }}</span>
            <span class="text-caption font-italic text--disabled"> {{ $t("plexus.fxNdf.fixingConventionNote") }}</span>
            <div>

                <v-radio-group v-model="maturityTenor" @change="onTenorChange" class="mt-0" mandatory row
                    :disabled="loadingNewRfq || gridActive">
                    <v-radio class="radio-label" label="D+15" value="d+15"> </v-radio>
                    <v-radio class="radio-label" label="D+30" value="d+30"> </v-radio>
                    <v-radio class="radio-label" label="D+45" value="d+45"> </v-radio>
                </v-radio-group>
                <div v-if="needsCalendar">

                    <v-menu v-model="menu" :close-on-content-click="false" transition="slide-y-transition" offset-y
                        bottom min-width="auto" :disabled="loadingNewRfq || gridActive">
                        <template v-slot:activator="{ on, attrs }">
                            <span class="text-caption font-italic text--disabled">
                                {{holidayWarningText}}
                            </span>
                        </template>

                        <v-date-picker color="primary" hide-details no-title v-model="maturityDate" :min="calendarMin"
                            :max="calendarMax" :allowed-dates="isAllowedMaturityDate"
                            @input="onMaturityDateSelected"></v-date-picker>
                    </v-menu>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { isMaturityHoliday, getDateYYYYMMDD } from '@/utils/utils';

// business-day count for each tenor, and the upper bound of the fallback
// calendar range shown when the tenor's date lands on a holiday
const TENOR_DAYS = { "d+15": 15, "d+30": 30, "d+45": 45 };
const TENOR_RANGE_END_DAYS = { "d+15": 30, "d+30": 45, "d+45": 60 };

function findFirstAllowedDate(startDays, endDays) {
    for (let days = startDays; days <= endDays; days++) {
        const dateStr = getDateYYYYMMDD(days, true);
        if (!isMaturityHoliday(dateStr)) return dateStr;
    }
    return getDateYYYYMMDD(endDays, true);
}

export default {
    props: ["loadingNewRfq", "gridActive"],
    emits: ["ccy", "side", "maturityDate"],
    data() {
        return {
            menu: false,
            side: "BUY",
            ccy: "USD",
            maturityTenor: "d+15",
            maturityDate: "",
            needsCalendar: false,
            calendarMin: "",
            calendarMax: "",
        };
    },
    mounted() {
        this.onTenorChange();
    },
    computed: {
        locale() {
            const savedLocale = localStorage.getItem("userLanguage");
            return savedLocale || "en";
        },
        holidayWarningText() {
            const days = this.convertDays(this.maturityTenor)
            const date = getDateYYYYMMDD(days,true)
            if (this.isFixingDateHoliday(date)) return this.$t("plexus.fxNdf.fixingDateHoliday")
            return this.$t("plexus.fxNdf.tenorHoliday", { tenor: this.maturityTenor.toUpperCase() });
        }
    },
    methods: {
        isAllowedMaturityDate(date) {
            return !isMaturityHoliday(date);
        },
        isFixingDateHoliday(maturityDate) {
            if (!maturityDate) return false;

            const [year, month, day] = maturityDate.split("-").map(Number);

            const fixingDate = new Date(year, month - 1, day);
            fixingDate.setDate(fixingDate.getDate() - 1);

            return isMaturityHoliday(fixingDate);
        },
        convertDays(tenor) {
            const days = TENOR_DAYS[tenor];
            return days
        },
        onTenorChange() {
            const days = this.convertDays(this.maturityTenor)
            const rangeEndDays = TENOR_RANGE_END_DAYS[this.maturityTenor];
            const dateStr = getDateYYYYMMDD(days, true);
            const fixingDate = this.isFixingDateHoliday(dateStr)
            if (isMaturityHoliday(dateStr) || fixingDate) {
                this.needsCalendar = true;
                this.calendarMin = dateStr;
                this.calendarMax = getDateYYYYMMDD(rangeEndDays, true);
                this.maturityDate = findFirstAllowedDate(days, rangeEndDays);
                this.menu = true;
            } else {
                this.needsCalendar = false;
                this.maturityDate = dateStr;
                this.menu = false;
            }

            this.onEmitChanges();
        },
        onMaturityDateSelected(date) {
            this.menu = false;
            this.maturityDate = date;
            this.onEmitChanges();
        },
        onEmitChanges() {
            const ready = this.side && this.ccy && this.maturityDate;
            if(!ready) return;

            this.$emit("ndfChange", {
                side: this.side,
                ccy: this.ccy,
                maturityDate: this.maturityDate,
            });
        },
    },
};
</script>
